<template>
  <svg
    v-show="displayStamp"
    class="verification-stamp"
    :class="getStampClass"
    :style="getStampSize"
  >
    <use :xlink:href="getStampIcon"></use>
  </svg>
</template>

<script>

const ME_ROLE_DOCUMENTS = 5;

const ICONS = {
  NON_VERIFIED: '/images/svg-symbols.svg#stamp-non_verified',
  VERIFIED: '/images/svg-symbols.svg#stamp-totally_verified'
}

export default {

  props: {
    /**
     * The stamp size in pixels.
     */
    size: {
      type: String,
      default: '24px'
    }
  },

  data() {
    return {
      hasDocumentsApproved: null,
      displayStamp: false
    }
  },

  computed: {
    /**
     * Return the stamp class based on the user roles.
     * @returns {String}
     */
    getStampClass() {
      const defaultClass = 'verification-stamp__icon';

      if(this.isVerified ) {
        return defaultClass;
      }

      return `${defaultClass}--non_verified`;
    },

    /**
     * Get the correct stamp icon name based on the user roles.
     * @returns {String} The stamp icon name.
     */
    getStampIcon() {
      /**
       * While the user roles are not loaded, the stamp won't be displayed.
       */
      if (this.isLoadingRoles) {
        return '';
      }

      return ICONS[this.getUserStatus];
    },

    /**
     * Returns the stamp width/height based on the size prop.
     * @return {String}
     */
    getStampSize() {
      return `width: ${this.size}; height: ${this.size};`;
    },

    /**
     * Checks if the user is verified
     * according to the user documents
     * @return {Boolean}
     */
    isVerified() {
      return Boolean(this.hasDocumentsApproved);
    },

    /**
     * Checks if the user roles are being loaded.
     * @return {Boolean}
     */
    isLoadingRoles() {
      return Boolean(this.hasDocumentsApproved === null);
    },

    /**
     * Get the user verification status based on the user documents and roles.
     * @returns {string} The user verification status.
     */
    getUserStatus() {
      return (this.isVerified) ? 'VERIFIED' : 'NON_VERIFIED';
    }
  },

  methods: {
    async getUserRoles() {
      this.loadingRoles = true;

      try {
        const {data: roles} = await axios.get('/api/v2/me/roles')

        this.checkDocumentsApproval(roles);

      } catch (error) {
        this.hasDocumentsApproved = false;
      }

      this.$emit('userVerificationStatus', this.getUserStatus);
    },

    /**
     * Check if the user has the documents approved.
     * @param {Array} roles
     */
    checkDocumentsApproval(roles) {
      this.hasDocumentsApproved = this.findUserRole(roles, ME_ROLE_DOCUMENTS);
    },

    /**
     * Checks the user roles according to the id provided
     * @param {Array} roles
     * @param {number} roleId
     * @returns {boolean}
     */
    findUserRole(roles, roleId) {
      return Boolean(roles.find(({id}) => id === roleId));
    }
  },

  created() {
    this.getUserRoles();
  },

  watch: {
    isLoadingRoles(value) {
      this.displayStamp = !value;
    }
  }
}

</script>

<style lang="stylus" scoped>
.verification-stamp
  width 16px
  height 16px

  @media screen and (min-width 940px)
    width 24px
    height 24px
</style>
